/**
 * What to say when an external run has no test rows to show.
 *
 * Pure, because the interesting part is the branching and not the markup. The
 * page previously had none: it rendered the "Partial results — recovered from
 * the tests that had already reported" banner and the "no results yet" empty
 * state one above the other, which is what production run `1fd8cd04` showed a
 * user. Both sentences were true; together they explained nothing and implied
 * the run might still be going.
 *
 * The three states are genuinely different events and deserve different words:
 * a run still in flight, a run that finished and reported nothing, and a run cut
 * short before a single test finished.
 */
export type EmptyResultsState = {
  title: string;
  description: string;
};

export function emptyResultsState(args: {
  /** `sealed` or `canceled` — the run will not produce anything further. */
  isFinished: boolean;
  /** The run ended early and its results were salvaged rather than reported. */
  resultsTruncated: boolean;
}): EmptyResultsState {
  if (args.resultsTruncated) {
    // Reached only with ZERO rows: truncated runs that recovered something show
    // their results plus the partial-results banner instead.
    //
    // Not "no results yet" — the run is over, and the reason it has none is that
    // it was stopped before anything finished, not that we are still waiting.
    // Naming the usual cause is what makes this actionable: at zero tests the
    // problem is almost always that the suite could not start.
    return {
      title: 'This run ended without reporting any tests',
      description:
        'The suite was stopped before any test completed, so there was nothing to recover. That usually means it could not start, or hung early — the run log is the place to look.',
    };
  }

  if (args.isFinished) {
    return {
      title: 'No tests reported',
      description: 'This run finished without reporting any test results.',
    };
  }

  return {
    title: 'No results yet',
    // Present tense on purpose: results now stream in as tests finish, so an
    // empty in-flight run really is a "wait a moment", which is exactly what
    // this state used to claim misleadingly for finished runs too.
    description: 'Tests appear here as they finish.',
  };
}
